import { ChangeDetectionStrategy, Component, computed, inject, input, OnInit, signal } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';

import { LayerListBloc } from '@connect-services/client/blocs/layer-list.bloc';
import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { SelectableHeaderReferenceDataComponent } from '@connect-services/client/components/modals/add-layer-modal/selectable-header-reference-data/selectable-header-reference-data.component';
import { NoDataToDisplayComponent } from '@connect-services/client/components/no-data-to-display/no-data-to-display.component';
import { AddLayerService } from '@connect-services/client/services/add-layer.service';
import { FetchAllService } from '@connect-services/client/services/fetch-all.service';
import { ExpansionPanelComponent } from '@connect-services/src/lib/components/expansion-panel/expansion-panel.component';
import { TabComponent, TabWithState } from '@connect-services/src/lib/components/tab/tab.component';
import {
    DepositFolder,
    DepositFolderControllerGetRequestParams,
    DepositFoldersService,
    Layer,
    LayerTopic,
    LayerTopicControllerGetRequestParams,
    LayerTopicsService,
} from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { LoadingComponent } from '@shared/client/components/loading/loading.component';
import { TabContentDirective } from '@shared/client/directives/tab-content.directive';
import { OrderEnum } from '@shared/client/models/pagination.model';
import { SnackBarService } from '@shared/client/services/snack-bar.service';

type DataByTopic = {
    depositFolders: DepositFolder[];
    topic: LayerTopic | null;
    topicName: string;
};

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ExpansionPanelComponent,
        LoadingComponent,
        NoDataToDisplayComponent,
        RessourcesModule,
        SelectableHeaderReferenceDataComponent,
        TabComponent,
        TabContentDirective,
    ],
    selector: 'app-select-reference-data-step',
    templateUrl: './select-reference-data-step.component.html',
})
export class SelectReferenceDataStepComponent implements OnInit {
    public readonly searchInput = input<string>('');

    protected readonly addLayerService = inject(AddLayerService);
    protected readonly data = signal<DepositFolder[]>([]);
    protected readonly depositFoldersByTopic = computed<DataByTopic[]>(() => {
        const layers: Layer[] = this.layerListBloc.value()?.items ?? [];
        const searchTerm: string = this.searchInput().trim().toLowerCase();

        return this.getDepositFoldersByTopic(layers, searchTerm, this.layersTopic(), this.data());
    });
    protected readonly isLoading = signal<boolean>(false);
    protected readonly selectedTabIndex = signal<number>(0);
    protected readonly tabsBadge = computed<boolean[]>(() => {
        const searchTerm = this.searchInput().trim();

        if (!searchTerm) {
            return this.depositFoldersByTopic().map(() => false);
        }

        return this.depositFoldersByTopic().map((item: DataByTopic) => {
            return !!item.depositFolders.length;
        });
    });
    protected readonly themeTabs = computed<TabWithState[]>(() =>
        this.depositFoldersByTopic().map((item: DataByTopic) => ({
            active: true,
            name: item.topicName,
        })),
    );

    private readonly depositFoldersService = inject(DepositFoldersService);
    private readonly fetchAllService = inject(FetchAllService);
    private readonly layerListBloc = inject(LayerListBloc);
    private readonly layersTopic = signal<LayerTopic[]>([]);
    private readonly layerTopicsService = inject(LayerTopicsService);
    private readonly projectBloc = inject(ProjectBloc);
    private readonly referenceDataType = 'reference-data';
    private readonly snackBarService = inject(SnackBarService);
    private readonly translocoService = inject(TranslocoService);

    public async ngOnInit(): Promise<void> {
        await this.getData();
        await this.getTopics();
    }

    protected changeTab(index: number): void {
        this.selectedTabIndex.set(index);
    }

    protected toggleDataSelected(data: DepositFolder): void {
        if (this.addLayerService.selectedDepositFolder()?.id === data.id) {
            this.addLayerService.selectedDepositFolder.set(undefined);

            return;
        }

        this.addLayerService.selectedDepositFolder.set(data);
    }

    private appendDataWithoutTopic(
        dataByTopic: DataByTopic[],
        dataWithoutTopic: DepositFolder[],
        searchTerm: string,
    ): DataByTopic[] {
        if (!dataWithoutTopic.length && !searchTerm) {
            return dataByTopic;
        }

        return [
            ...dataByTopic,
            {
                depositFolders: dataWithoutTopic,
                topic: null,
                topicName: this.translocoService.translate('other'),
            },
        ];
    }

    private buildDataByTopic(layers: Layer[], layersTopic: LayerTopic[], data: DepositFolder[]): DataByTopic[] {
        return layersTopic.map((layerTopic: LayerTopic) => {
            const topicDepositFolders = this.getTopicDepositFolders(data, layers, layerTopic);

            return {
                depositFolders: topicDepositFolders,
                topic: layerTopic,
                topicName: layerTopic.name,
            };
        });
    }

    private filterDepositFolders(depositFolders: DepositFolder[], searchTerm: string): DepositFolder[] {
        if (!searchTerm) {
            return depositFolders;
        }

        return depositFolders.filter((depositFolder) => {
            return depositFolder.name.toLowerCase().includes(searchTerm);
        });
    }

    private filterDepositFoldersInsideTopics(dataByTopic: DataByTopic[], searchTerm: string): DataByTopic[] {
        return dataByTopic.map((dataByTopic: DataByTopic) => {
            return {
                ...dataByTopic,
                depositFolders: this.filterDepositFolders(dataByTopic.depositFolders, searchTerm),
            };
        });
    }

    private async getData(): Promise<void> {
        this.isLoading.set(true);
        try {
            const data: DepositFolder[] = await this.fetchAllService.getAll<
                DepositFolderControllerGetRequestParams,
                DepositFolder
            >(this.depositFoldersService.depositFolderControllerGet.bind(this.depositFoldersService), {
                order: OrderEnum.ASC,
                orderBy: 'name',
                type: this.referenceDataType,
            });

            this.data.set(data);
        } catch {
            this.snackBarService.openErrorSnackBar('errorGET');
        } finally {
            this.isLoading.set(false);
        }
    }

    private getDataWithoutTopic(layers: Layer[], data: DepositFolder[]): DepositFolder[] {
        const usedDepositIds = new Set(
            layers
                .filter((layer): boolean => !!layer.layerTopic && !!layer.deposit)
                .map((layer): string | null => layer.deposit),
        );

        return data.filter((depositFolder): boolean => {
            return !depositFolder.deposit?.id || !usedDepositIds.has(depositFolder.deposit.id);
        });
    }

    private getDepositFoldersByTopic(
        layers: Layer[],
        searchTerm: string,
        layersTopic: LayerTopic[],
        data: DepositFolder[],
    ): DataByTopic[] {
        const dataByTopic: DataByTopic[] = this.buildDataByTopic(layers, layersTopic, data);
        const visibleDataByTopic: DataByTopic[] = dataByTopic.filter((item: DataByTopic) => item.depositFolders.length);
        const filteredDataByTopic: DataByTopic[] = this.filterDepositFoldersInsideTopics(
            visibleDataByTopic,
            searchTerm,
        );
        const dataWithoutTopic: DepositFolder[] = this.getDataWithoutTopic(layers, data);
        const filteredDataWithoutTopic: DepositFolder[] = this.filterDepositFolders(dataWithoutTopic, searchTerm);

        return this.appendDataWithoutTopic(filteredDataByTopic, filteredDataWithoutTopic, searchTerm);
    }

    private getTopicDepositFolders(data: DepositFolder[], layers: Layer[], layerTopic: LayerTopic): DepositFolder[] {
        const topicDepositIds = new Set(
            layers
                .filter((layer: Layer) => layer.layerTopic === layerTopic.id && layer.deposit != null)
                .map((layer: Layer) => layer.deposit),
        );

        return data.filter((depositFolder: DepositFolder) => {
            return !!depositFolder.deposit?.id && topicDepositIds.has(depositFolder.deposit.id);
        });
    }

    private async getTopics(): Promise<void> {
        this.isLoading.set(true);
        try {
            const projectId = this.projectBloc.value()!.id;

            const layersTopic = await this.fetchAllService.getAll<LayerTopicControllerGetRequestParams, LayerTopic>(
                this.layerTopicsService.layerTopicControllerGet.bind(this.layerTopicsService),
                {
                    order: OrderEnum.ASC,
                    orderBy: 'name',
                    projectId,
                },
            );
            this.layersTopic.set(layersTopic);
        } catch {
            this.snackBarService.openErrorSnackBar('errorGET');
        } finally {
            this.isLoading.set(false);
        }
    }
}
